/**
 * signingErrors — GDN-3288 sign-time compliance failure client helpers.
 *
 * Signing, amending, or co-signing a note has the backend render the
 * compliance PDF. When that render fails for a CONTENT reason the backend
 * answers with the standard v2 `ClientErrorResponse` shape and error_code
 * "pdf_render_failed" (422); when the compliance service itself is
 * unreachable it answers "compliance_unavailable" (502). For both codes the
 * backend `message` IS the user-facing copy — it names the offending section
 * and the recovery step — so the client surfaces it verbatim instead of the
 * generic "Failed to … Please try again." fallback.
 */

import { type ApiErrorBody, candidateErrorBodies } from '@features/admin-v2/utils/api-error';
import type { BulkCosignItemResponse } from '@store/generated/notes';

/** Error codes whose backend `message` is written to be shown to the user. */
export const USER_FACING_SIGNING_ERROR_CODES = [
  'pdf_render_failed',
  'compliance_unavailable',
  // GDN-3911: a supervisor pressed sign on a clinician supervisee's note that
  // the author has not submitted yet. The backend answers 403 and names the
  // author — "Waiting for {author} to submit this note for co-signature." —
  // which is the whole point of the message, so it is shown verbatim.
  'author_must_submit_first',
] as const;

export type UserFacingSigningErrorCode = (typeof USER_FACING_SIGNING_ERROR_CODES)[number];

/**
 * Snackbar options for these messages: they carry an instruction, so hold
 * them well past the app default (3s) — matches the repo's other
 * instruction-bearing error toasts.
 */
export const SIGNING_ERROR_SNACKBAR_OPTIONS = {
  variant: 'error',
  autoHideDuration: 10000,
  preventDuplicate: true,
} as const;

export const isUserFacingSigningErrorCode = (
  code: unknown,
): code is UserFacingSigningErrorCode =>
  typeof code === 'string' &&
  (USER_FACING_SIGNING_ERROR_CODES as readonly string[]).includes(code);

/**
 * The backend's message for a `pdf_render_failed` / `compliance_unavailable`
 * body, or null when the body is anything else (or carries no message).
 * Shared by the single-note mutation error path and the bulk per-item path.
 */
const getUserFacingMessage = (body: {
  error_code?: unknown;
  message?: unknown;
}): string | null => {
  if (!isUserFacingSigningErrorCode(body.error_code)) return null;
  return typeof body.message === 'string' && body.message.trim() ? body.message : null;
};

/**
 * The backend body of an RTK Query rejection. `auth0BaseQuery` re-wraps every
 * failure as `{ status, data: { status, message, data: <backend body> } }`, so
 * in the running app the body is at `error.data.data`; hand-built mocks pass
 * the flat `error.data`. `candidateErrorBodies` yields both depths; prefer the
 * one carrying an `error_code` — the wrapper's `message` is synthesized by the
 * base query, the coded body's is the backend's own.
 */
const getErrorBody = (error: unknown): { error_code?: unknown; message?: unknown } | null => {
  const bodies = candidateErrorBodies(error);
  return bodies.find(body => typeof body.error_code === 'string') ?? bodies[0] ?? null;
};

/**
 * The backend's stable `error_code` from a mutation rejection, for analytics
 * only — a bare code (e.g. "pdf_render_failed"), never the message, which
 * names note sections. Null when the rejection carries no string code.
 */
export const getSigningErrorCode = (error: unknown): string | null => {
  const code = getErrorBody(error)?.error_code;
  return typeof code === 'string' && code ? code : null;
};

/**
 * Recognize a sign / amend / co-sign mutation rejection (RTK Query's
 * `{ status, data }`) whose body is one of the user-facing codes, and return
 * the backend message to show. Null for every other error so callers keep
 * their generic fallback. Check the future-date-of-service 409 first — that
 * one opens a dialog rather than a toast.
 */
export const getSigningErrorMessage = (error: unknown): string | null => {
  const body = getErrorBody(error);
  return body ? getUserFacingMessage(body) : null;
};

/**
 * GDN-3911: the backend's message when it refused because the note's author —
 * a clinician supervisee — has not submitted it for co-signature yet.
 *
 * This answer is DEFINITIVE about something the client cannot otherwise know:
 * that the author is a supervisee rather than a clinical_staff drafter whose
 * note a supervisor could take over (the two are indistinguishable on the
 * legacy `requires_cosigning && !documented_by` shape). Callers retain it for
 * the note so the affordance can settle into its real disabled state.
 */
export const getAuthorMustSubmitMessage = (error: unknown): string | null => {
  const body = getErrorBody(error);
  if (!body || body.error_code !== 'author_must_submit_first') return null;
  return typeof body.message === 'string' && body.message.trim() ? body.message : null;
};

/**
 * The backend's own message from a 403 refusal, at either wrapper depth.
 *
 * GDN-3911: the finalize endpoints refuse for reasons only the backend can
 * state — who still has to submit, whose supervisee this is — and those
 * sentences are written for the provider reading them. Any 403 carrying a
 * message therefore shows that message instead of "Failed to sign note."
 *
 * The status check is not optional: a code-free transport failure that happens
 * to echo a body must never be read as the backend's considered refusal. And
 * the body carrying an `error_code` is preferred over the wrapper's, whose
 * `message` is synthesized by `auth0BaseQuery` rather than written by anyone.
 *
 * This is deliberately broader than `getSigningErrorMessage`, which only fires
 * for the enumerated codes. Callers try the coded path first (it earns the
 * longer, instruction-bearing snackbar) and fall back to this one.
 */
export const getForbiddenMessage = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  if ((error as { status?: unknown }).status !== 403) return null;

  const bodies = candidateErrorBodies(error);
  const withMessage = bodies.filter(
    (body): body is ApiErrorBody & { message: string } =>
      typeof body.message === 'string' && body.message.trim().length > 0,
  );
  if (withMessage.length === 0) return null;

  // A coded body is the backend's own, so it wins outright. Otherwise take the
  // INNERMOST message: `candidateErrorBodies` yields [wrapper, backend], and
  // the wrapper's message is synthesized by auth0BaseQuery ("Request failed"),
  // which is precisely the string that must never reach a provider.
  const coded = withMessage.find(body => typeof body.error_code === 'string');
  return (coded ?? withMessage[withMessage.length - 1]).message;
};

/**
 * Bulk co-sign variant: the per-note result item carries `error_code` and
 * `message` directly (no HTTP envelope). Null unless the item failed with one
 * of the user-facing codes.
 */
export const getBulkCosignItemErrorMessage = (
  item: Pick<BulkCosignItemResponse, 'success' | 'error_code' | 'message'>,
): string | null => (item.success ? null : getUserFacingMessage(item));
